import { webRoutes } from '@runhuman/shared';
import { QueueList } from '../views/tester/QueueList';
import { ActiveJobPanel } from '../views/tester/ActiveJobPanel';
import { PresenceRoster } from '../views/tester/PresenceRoster';
import { StuckJobsAlertStrip } from '../views/admin/StuckJobsAlertStrip';
import { NotAuthorizedView } from '../views/NotAuthorizedView';
import { UnlinkedView } from '../views/UnlinkedView';
import { ActivityIdentityHeader } from '../views/ActivityIdentityHeader';
import { InstanceParticipantsHeader } from '../views/InstanceParticipantsHeader';
import { FloorManagerOnDutyTile } from '../views/FloorManagerOnDutyTile';
import { WebPopoutButton } from '../views/WebPopoutButton';
import {
  fixtureQueue,
  fixtureActiveJob,
  fixturePresence,
  fixturePresenceTiming,
  fixtureStuckJobs,
  fixtureInstanceParticipants,
  fixtureFloorManagersOnDuty,
  FIXTURE_PREVIEW_DISCORD_USER_ID,
} from './fixtures';

/**
 * Linked Discord ids of the fixture on-duty FMs (#5169) — drives the badge
 * gate in the preview `<InstanceParticipantsHeader>`. Mirrors the production
 * `resolveFloorManagerDiscordUserIds` derivation (drop unlinked FMs).
 */
const fixtureFmDiscordUserIds = fixtureFloorManagersOnDuty
  .map((fm) => fm.discordUserId)
  .filter((id): id is string => id !== null);

/**
 * Identity-header fixture for visual previews — alias case (tester) renders
 * a tester profile alias; the admin/unauthorized cases would fall back to an
 * email in production but the preview catalog uses an alias for both since
 * the identity-line layout is identical regardless of source.
 */
const FIXTURE_LINKED_USER_ALIAS = { userId: 'preview-user-alias', displayName: 'aaron-the-tester' } as const;
const FIXTURE_LINKED_USER_EMAIL = { userId: 'preview-user-email', displayName: 'admin@example.com' } as const;

/**
 * Dev-only visual preview harness.
 *
 * Bypasses `useActivitySession` (which hangs forever outside a real Discord
 * iframe) and renders the same child components used by `<TesterView>`
 * against fixture data. Mounted from `App.tsx` when `import.meta.env.DEV`
 * is true and the URL has `?preview=<view>`.
 *
 * Visit `http://localhost:3200/?preview=tester` (or `tester-admin`,
 * `tester-non-tester-admin`, `unlinked`, `unauthorized`, `loading`, `error`)
 * after `npm run dev --workspace=@runhuman/discord-activity` to walk every
 * surface visually.
 *
 * After #4172 the Ops Console flip was retired; every linked user lands on
 * the single consolidated tester view, and admin widgets (FM tile is for
 * everyone; stuck-jobs strip + "Open jobs in admin" header action are
 * admin-only) are layered inline rather than hidden behind a second surface.
 */
export type PreviewView =
  | 'tester'
  | 'tester-empty'
  | 'tester-admin'
  | 'tester-non-tester-admin'
  | 'unlinked'
  | 'unauthorized'
  | 'loading'
  | 'error';

const VALID_VIEWS: ReadonlySet<string> = new Set<PreviewView>([
  'tester',
  'tester-empty',
  'tester-admin',
  'tester-non-tester-admin',
  'unlinked',
  'unauthorized',
  'loading',
  'error',
]);

export function getPreviewViewFromUrl(): PreviewView | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === 'undefined') return null;
  const param = new URLSearchParams(window.location.search).get('preview');
  if (param && VALID_VIEWS.has(param)) return param as PreviewView;
  return null;
}

interface Props {
  readonly view: PreviewView;
}

export function PreviewHarness({ view }: Props) {
  switch (view) {
    case 'tester':
      return (
        <>
          <ActivityIdentityHeader linkedUser={FIXTURE_LINKED_USER_ALIAS} />
          <div className="view view--tester">
            <header className="view__header">
              <h1>Runhuman Tester Lounge</h1>
            </header>
            <FloorManagerOnDutyTile floorManagersOnDuty={fixtureFloorManagersOnDuty} />
            <ActiveJobPanel activeJob={fixtureActiveJob} />
            <QueueList queue={fixtureQueue} onClaim={() => {}} disabled={true} />
            <PresenceRoster testers={fixturePresence} timing={fixturePresenceTiming} />
          </div>
        </>
      );
    case 'tester-empty':
      return (
        <>
          <ActivityIdentityHeader linkedUser={FIXTURE_LINKED_USER_ALIAS} />
          <div className="view view--tester">
            <header className="view__header">
              <h1>Runhuman Tester Lounge</h1>
            </header>
            <FloorManagerOnDutyTile floorManagersOnDuty={[]} />
            <QueueList
              queue={{ available: [], inProgress: [], needsLateSubmission: [], orgCompletedCounts: {} }}
              onClaim={() => {}}
            />
            <PresenceRoster testers={[]} timing={fixturePresenceTiming} />
          </div>
        </>
      );
    case 'tester-admin':
      return (
        <>
          <InstanceParticipantsHeader
            participants={fixtureInstanceParticipants}
            currentUserId={FIXTURE_PREVIEW_DISCORD_USER_ID}
            floorManagerDiscordUserIds={fixtureFmDiscordUserIds}
          />
          <ActivityIdentityHeader linkedUser={FIXTURE_LINKED_USER_ALIAS} />
          <div className="view view--tester">
            <header className="view__header">
              <h1>Runhuman Tester Lounge</h1>
              <div className="view__header-actions">
                <WebPopoutButton path={webRoutes.adminJobs.build()}>
                  Open jobs in admin
                </WebPopoutButton>
              </div>
            </header>
            <FloorManagerOnDutyTile floorManagersOnDuty={fixtureFloorManagersOnDuty} />
            <StuckJobsAlertStrip
              stuckJobs={fixtureStuckJobs}
              busyJobId={null}
              onRelease={() => {}}
              onTerminate={() => {}}
            />
            <ActiveJobPanel activeJob={fixtureActiveJob} />
            <QueueList queue={fixtureQueue} onClaim={() => {}} />
            <PresenceRoster testers={fixturePresence} timing={fixturePresenceTiming} showAdminLink />
          </div>
        </>
      );
    case 'tester-non-tester-admin':
      return (
        <>
          <InstanceParticipantsHeader
            participants={fixtureInstanceParticipants}
            currentUserId={FIXTURE_PREVIEW_DISCORD_USER_ID}
            floorManagerDiscordUserIds={fixtureFmDiscordUserIds}
          />
          <ActivityIdentityHeader linkedUser={FIXTURE_LINKED_USER_EMAIL} />
          <div className="view view--tester">
            <header className="view__header">
              <h1>Runhuman Tester Lounge</h1>
              <div className="view__header-actions">
                <WebPopoutButton path={webRoutes.adminJobs.build()}>
                  Open jobs in admin
                </WebPopoutButton>
              </div>
            </header>
            <FloorManagerOnDutyTile floorManagersOnDuty={fixtureFloorManagersOnDuty} />
            <StuckJobsAlertStrip
              stuckJobs={fixtureStuckJobs}
              busyJobId={null}
              onRelease={() => {}}
              onTerminate={() => {}}
            />
            <QueueList
              queue={fixtureQueue}
              onClaim={() => {}}
              claimDisabledReason="You're not a tester — only testers can claim jobs."
            />
            <PresenceRoster testers={fixturePresence} timing={fixturePresenceTiming} showAdminLink />
          </div>
        </>
      );
    case 'unlinked':
      return <UnlinkedView onCheckAgain={() => {}} isChecking={false} />;
    case 'unauthorized':
      return <NotAuthorizedView />;
    case 'loading':
      return <div className="view view--loading">Connecting to Discord…</div>;
    case 'error':
      return (
        <div className="view view--error">
          <h1>Couldn't load the Activity</h1>
          <p>Discord token exchange failed: 400 invalid_grant</p>
        </div>
      );
  }
}
